import React, { useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { doc, getDoc } from "firebase/firestore";

import { ArrowLeft, Plus } from "lucide-react";

import { HomeLayout } from "../components/layout/HomeLayout";

import { JoinClassroomPanel } from "../components/dashboard/JoinClassroomPanel";
import { useBordonaAccount } from "../hooks/useBordonaAccount";

import { useStudentAuth } from "../hooks/useStudentAuth";

import { useStudentClassrooms } from "../hooks/useStudentClassrooms";

import { useTeacherClassrooms } from "../hooks/useTeacherClassrooms";

import { useTeacherId } from "../hooks/useTeacherId";

import { db } from "../../firebase/config";

import { normalizeClassroomCode } from "../lib/classroomEngine";

import type { Task } from "../lib/taskEngine";

import { HOME_THEME as C } from "../lib/homeTheme";



export const ClassroomsPage: React.FC = () => {

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { role, isLoading: accountLoading } = useBordonaAccount();

  const { profile, user } = useStudentAuth();

  const { teacherId, teacherName } = useTeacherId();

  const studentId = profile?.uid ?? user?.uid ?? "";

  const studentName = profile

    ? `${profile.firstName} ${profile.lastName}`.trim()

    : user?.displayName?.trim() || "Estudiante";



  const isTeacher = role === "teacher";

  const { memberships, loading: studentLoading } = useStudentClassrooms(

    studentId || undefined,

    studentName

  );

  const {

    classrooms,

    membersByClassroom,

    creating,

    createClassroom,

    error: teacherError,

    loading: teacherLoading,

  } = useTeacherClassrooms(teacherId, teacherName);



  const [newClassTitle, setNewClassTitle] = useState("");

  const [createdCode, setCreatedCode] = useState<string | null>(null);



  useEffect(() => {

    const taskCode = searchParams.get("task")?.trim().toUpperCase();

    if (!taskCode || taskCode.length < 4) return;



    void (async () => {

      try {

        const snap = await getDoc(doc(db, "bordona_tasks", taskCode));

        if (snap.exists()) {

          const task = snap.data() as Task;

          if (task.classroomId) {

            navigate(

              `/clases/${normalizeClassroomCode(task.classroomId)}?task=${taskCode}`,

              { replace: true }

            );

            return;

          }

        }

        navigate("/clases", {

          replace: true,

          state: {

            taskError: "Entra a la clase correspondiente para abrir esta tarea.",

          },

        });

      } catch {

        navigate("/clases", { replace: true });

      }

    })();

  }, [searchParams, navigate]);



  const handleCreate = async () => {

    const code = await createClassroom(newClassTitle);

    if (code) {

      setCreatedCode(code);

      setNewClassTitle("");

    }

  };



  const loading = isTeacher ? teacherLoading : studentLoading;

  const listItems = isTeacher

    ? classrooms.map((cls) => ({

        id: cls.id,

        title: cls.title,

        code: cls.id,

        meta: `${membersByClassroom(cls.id).length} alumno${membersByClassroom(cls.id).length !== 1 ? "s" : ""}`,

      }))

    : memberships.map((m) => ({

        id: m.id,

        title: m.classroomTitle,

        code: m.classroomCode,

        meta: m.teacherName || "Profesor",

      }));



  if (accountLoading) {
    return (
      <HomeLayout>
        <div className="flex h-full items-start justify-start px-6 md:px-12 lg:px-24 pt-32">
          <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-[#c4b5fd] animate-spin" />
        </div>
      </HomeLayout>
    );
  }

  return (

    <HomeLayout>

      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-24 py-10 pb-24">

        <button

          type="button"

          onClick={() => navigate("/home")}

          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#c4b5fd] mb-12"

          style={{ color: C.muted }}

        >

          <ArrowLeft className="h-4 w-4" />

          Inicio

        </button>



        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-16 xl:gap-24">

          {/* Columna izquierda — identidad y acciones */}

          <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">

            <h1

              className="font-light tracking-tight text-left text-[clamp(2.5rem,6vw,4.5rem)] leading-[0.95] bg-clip-text text-transparent"

              style={{ backgroundImage: C.textGradient }}

            >

              Mis clases

            </h1>



            <p className="mt-5 max-w-sm text-sm leading-relaxed text-left" style={{ color: C.muted }}>

              {isTeacher

                ? "Crea clases y entra a cada una para gestionar tareas y entregas."

                : "Únete con el código de tu profesor. Las tareas viven dentro de cada clase."}

            </p>



            {isTeacher && (

              <div className="mt-10 space-y-4 text-left">

                <label

                  className="block text-[10px] tracking-[0.22em] uppercase"

                  style={{ color: C.dim }}

                >

                  Crear clase

                </label>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">

                  <input

                    type="text"

                    value={newClassTitle}

                    onChange={(e) => setNewClassTitle(e.target.value)}

                    placeholder="Nombre, ej. Guitarra 2ºA"

                    className="w-full border-0 border-b bg-transparent pb-2 text-sm text-left focus:outline-none focus:border-[#c4b5fd]"

                    style={{ color: C.text, borderColor: C.lineItem }}

                  />

                  <button

                    type="button"

                    onClick={handleCreate}

                    disabled={creating || !newClassTitle.trim()}

                    className="inline-flex shrink-0 items-center gap-2 text-[11px] tracking-[0.14em] uppercase disabled:opacity-40 hover:text-[#99f6e4] transition-colors"

                    style={{ color: C.text }}

                  >

                    <Plus className="h-4 w-4" />

                    {creating ? "Creando…" : "Crear"}

                  </button>

                </div>

                {teacherError && <p className="text-sm text-rose-300 text-left">{teacherError}</p>}

                {createdCode && (

                  <p className="text-sm text-left" style={{ color: C.mint }}>

                    Clase creada ·{" "}

                    <span className="font-mono tracking-wider">{createdCode}</span>

                  </p>

                )}

              </div>

            )}



            {role === "student" && studentId ? (
              <div className="relative z-20 mt-10 text-left">
                <JoinClassroomPanel
                  studentId={studentId}
                  studentName={studentName}
                  variant="minimal"
                />
              </div>
            ) : role === "student" ? (
              <p className="mt-10 text-sm text-left" style={{ color: C.muted }}>
                Inicia sesión con tu cuenta Edusico para unirte a una clase con el código de tu
                profesor.
              </p>
            ) : null}



            <p className="mt-12 text-xs leading-relaxed text-left" style={{ color: C.dim }}>

              {isTeacher

                ? "Selecciona una clase en el índice para ver tareas y entregas."

                : "Entra a una clase para ver tus tareas asignadas."}

            </p>

          </div>



          {/* Columna derecha — índice editorial */}

          <div className="mt-14 lg:mt-0 lg:col-span-8">

            <p

              className="mb-8 text-[10px] tracking-[0.22em] uppercase text-left"

              style={{ color: C.dim }}

            >

              {isTeacher ? "Tus clases" : "Clases a las que perteneces"}

            </p>



            {loading && (

              <p className="py-8 text-sm animate-pulse text-left" style={{ color: C.dim }}>

                Cargando…

              </p>

            )}



            {!loading && listItems.length === 0 && (

              <p className="py-8 text-sm text-left" style={{ color: C.dim }}>

                {isTeacher

                  ? "Aún no has creado ninguna clase."

                  : "Usa el código de 8 caracteres para unirte a tu primera clase."}

              </p>

            )}



            {!loading && listItems.length > 0 && (

              <ul>

                {listItems.map((item, i) => (

                  <ClassroomIndexRow

                    key={item.id}

                    index={i + 1}

                    title={item.title}

                    code={item.code}

                    meta={item.meta}

                    onEnter={() => navigate(`/clases/${item.code}`)}

                  />

                ))}

              </ul>

            )}

          </div>

        </div>

      </div>

    </HomeLayout>

  );

};



function ClassroomIndexRow({

  index,

  title,

  code,

  meta,

  onEnter,

}: {

  index: number;

  title: string;

  code: string;

  meta: string;

  onEnter: () => void;

}) {

  return (

    <li style={{ borderBottom: `1px solid ${C.lineItem}` }}>

      <button

        type="button"

        onClick={onEnter}

        className="group flex w-full items-baseline gap-4 md:gap-8 py-5 md:py-6 text-left transition-all duration-300 hover:pl-2 hover:bg-white/[0.02]"

      >

        <span className="w-8 shrink-0 text-xs tabular-nums tracking-wider" style={{ color: C.dim }}>

          {String(index).padStart(2, "0")}

        </span>

        <span className="min-w-0 flex-1 text-left">

          <span

            className="block font-serif font-light tracking-tight text-[clamp(1.25rem,2.5vw,2rem)] group-hover:text-[#ddd6fe] transition-colors truncate"

            style={{ color: C.text }}

          >

            {title}

          </span>

          <span className="mt-1 block text-xs tracking-wide truncate" style={{ color: C.muted }}>

            {meta}

          </span>

        </span>

        <span

          className="shrink-0 font-mono text-xs tracking-[0.18em] uppercase"

          style={{ color: C.mint }}

        >

          {code}

        </span>

      </button>

    </li>

  );

}



export default ClassroomsPage;

